/**
 * A failure as an attorney reads it: `text` in plain words, never cut, and
 * the server's own wording as `detail` when `text` had to replace it.
 */
export type PlainError = { text: string; detail: string | null };

type Rule = { test: RegExp; text: (m: RegExpMatchArray) => string };

/** Known server errors, said plainly. */
const RULES: Rule[] = [
  {
    test: /^(\w+) handback URL is not configured/i,
    text: (m) =>
      `${m[1]} couldn't send its results back (the server's public URL isn't set)`
  },
  {
    test: /handback URL is not configured/i,
    text: () =>
      "Casey couldn't send the results back (the server's public URL isn't set)"
  },
  {
    test: /callback address \(.*\) cannot be reached/i,
    text: () =>
      "Casey couldn't send the results back (the server's address can't be reached)"
  },
  {
    test: /\btimed? ?out\b|timeout/i,
    text: () => "a service CaseBlink relies on took too long to answer"
  },
  {
    test: /connection (refused|reset|error|aborted)|max retries exceeded|name or service not known/i,
    text: () => "a service CaseBlink relies on couldn't be reached"
  }
];

/** Wording only an engineer reads: a stack, a class name, raw data. */
const TECHNICAL =
  /traceback|^\s*\w+(Error|Exception)\b|errno|[{}[\]<>]|https?:\/\/|\bNone\b/i;

/** "ValueError: bad input" → "bad input". */
const withoutClass = (text: string) =>
  text.replace(/^\s*[A-Z]\w*(Error|Exception):\s*/, "");

const tidy = (text: string) =>
  text.replace(/\s+/g, " ").trim().replace(/\.$/, "");

export const plainError = (
  raw: string | null | undefined
): PlainError | null => {
  const full = tidy(raw ?? "");
  if (!full) return null;
  const rule = RULES.find((r) => r.test.test(full));
  if (rule)
    return {
      text: rule.text(full.match(rule.test) as RegExpMatchArray),
      detail: full
    };
  const message = tidy(withoutClass(full));
  if (message && !TECHNICAL.test(message))
    return { text: message, detail: message === full ? null : full };
  return { text: "an unexpected error stopped it", detail: full };
};
